import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box } from "@mui/material";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import type { CalledOffShift } from "../../../../utils/models";
import { JumpingDots } from "../../../UIToolkit/Animations/JumpingDots";
import { EmptyState } from "../../../UIToolkit/molecules/EmptyState";
import { MobileCallInItem } from "./MobileCallInItem";

type Props = {
	callIns: CalledOffShift[];
	isLoading: boolean;
	isError: boolean;
};

/** The manager-facing "who called out" list for one staffing day. Ordered earliest shift first. */
export const MobileCallInsList: FC<Props> = ({ callIns, isLoading, isError }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { track } = useAnalytics();
	const navigate = useNavigate();

	// A call-in is usually the start of a conversation about the person, not the shift — so the card
	// opens the employee's profile (where the full call-in history lives) rather than the removed shift,
	// which no longer exists to navigate to.
	const handleClick = useCallback(
		(nurseId: number) => {
			track("mobile_call_in_profile_opened", { nurse_id: nurseId, department_id: chosenDepartment.department_id });
			navigate(`/${chosenDepartment.department_id}/nurse/${nurseId}`);
		},
		[track, navigate, chosenDepartment.department_id],
	);

	if (isLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 4 }} data-testid="mobile-call-ins-loading">
				<JumpingDots />
			</Box>
		);
	}

	if (isError) {
		return <EmptyState title={t("call.ins.load.error")} icon={<PhoneOutlinedIcon />} dataTestId="mobile-call-ins-error" />;
	}

	if (!callIns.length) {
		return (
			<EmptyState title={t("call.ins.none.for.day")} hint={t("call.ins.none.for.day.hint")} icon={<PhoneOutlinedIcon />} dataTestId="mobile-call-ins-empty" />
		);
	}

	return (
		<Box data-testid="mobile-call-ins-list">
			{callIns.map((callIn) => (
				<MobileCallInItem key={callIn.id} callIn={callIn} onClick={handleClick} />
			))}
		</Box>
	);
};
